import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_AR,
  RECEIVE_ALL_AR,
  RECEIVE_AR_METADATA,
  INCREMENT_AR_COUNT,
  RECEIVE_AR_LINES,
  RECEIVE_ALL_AR_LINES,
  RECEIVING_AR_LINES,
  RECEIVE_AR_LINES_METADATA,
  RECEIVE_AR_LINES_FAILURE,
  SAVING_AR_LINES,
  UPDATING_AR_LINES,
  CLEAR_AR_STATE,
  CLEAR_STATE,
  RESET_AR_META,
  UPDATE_CURRENT_AR_PAGE,
  DELETE_AR_LINES,
  CLEAR_AR_TRX_LINES,
  CLEAR_FILTER_AR_QUERY,
  FILTER_AR_QUERY
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { fetchLines, getLineIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  clearState,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const lineSchema = new schema.Entity('lines')
const arSchema = new schema.Entity('ars', { lines: new schema.Array(lineSchema) })
const arListSchema = new schema.Array(arSchema)

const state = {
  data: {},
  all: [],
  allLines: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null,
  currentPage: 1,
  filterInfo: {}
}

const actions = {
  /**
   * Fetches one or more ARs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchArsWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = null
    }
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('ars', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_AR_META)
      let { results, next, previous, count } = resp
      const { result, entities } = normalize(results, arListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      this.commit(RECEIVE_AR, { data: entities.ars })
      this.commit(RECEIVE_AR_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_AR_PAGE, 1)
      if (storeLines) {
        const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.ars), type: 'ar' })
        this.commit(RECEIVE_AR_LINES, { id: null, data: lines })
      }
      return { query, count, entities }
    } catch (e) {
      console.log(e)
      if (e.status === 403) {
        window.location.href = '/403'
      }
      return e
    }
  },
  /**
   * Fetches all available ARs
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllArs (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrl('ars?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVE_ALL_AR, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Fetches all available AR lines
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllArLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_AR_LINES, true)
    try {
      const resp = await Vue.http.get(buildUrl('lines/ar?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVING_AR_LINES, false)
      this.commit(RECEIVE_ALL_AR_LINES, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      this.commit(RECEIVING_AR_LINES, false)
      this.commit(RECEIVE_AR_LINES_FAILURE, e.statusText)
    }
  },
  /**
   * Saves an AR transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async saveAr (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('ars', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save AR`)
      }
      const { entities, result } = normalize(resp, arSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving AR`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.ars), type: 'ar' })
      this.commit(RECEIVE_AR, { data: entities.ars })
      this.commit(RECEIVE_AR_LINES, { id: null, data: lines })
      this.commit(INCREMENT_AR_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates an AP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updateAr (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`ars/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update AR with id ${id}`)
      }
      const { entities, result } = normalize(resp, arSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating AR with id ${id}`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.ars), type: 'ar' })
      this.commit(RECEIVE_AR, { data: entities.ars })
      this.commit(RECEIVE_AR_LINES, { id: null, data: lines })
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Deletes an AP transaction
   * @param store
   * @param id
   * @return {Promise<*>}
   */
  async deleteAr (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      return await Vue.http.delete(buildUrlWithQuery(`ars/${id}`, null, instanceUrl), { headers })
        .then(checkStatus)
    } catch (e) {
      console.log(e)
      return e
    }
  },
  async fetchInheritedMarkups (store, payload) {
    const { query } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      return await Vue.http.get(buildUrlWithQuery('markups/inherited_markup', query, instanceUrl), { headers })
    } catch (e) {
      console.log(e)
    }
  },
  clearArState (store, payload = null) {
    return this.commit(CLEAR_AR_STATE, payload)
  },
  clearArTransactionLines (store, apId) {
    this.commit(CLEAR_AR_TRX_LINES, apId)
  },
  resetArMeta () {
    this.commit(RESET_AR_META)
  },
  setArFilterInfo (store, payload) {
    this.commit(FILTER_AR_QUERY, payload)
  },
  clearSetArFilterInfo (store) {
    this.commit(CLEAR_FILTER_AR_QUERY)
  }
}

const getters = {
  getArFilterInfo: (state) => {
    return state.filterInfo
  },
  getArs: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getArById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllArs: (state) => {
    return state.all || []
  },
  getArMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getArLines: (state) => {
    return state.lines || {}
  },
  getAllArLines: (state) => {
    return state.allLines || []
  },
  getArLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getArLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextArLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevArLineSet: (state) => {
    return state.prevLineUrl
  },
  getArsByNumber: (state) => (number) => {
    return Object.values(state.data).filter(v => v.number.includes(number))
  }
}

const mutations = {
  setAppliedFilters (state, filters) {
    state.appliedFilters = { ...filters }
  },
  [CLEAR_FILTER_AR_QUERY] (state) {
    state.filterInfo = {}
  },
  [FILTER_AR_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfo.keyword = query.keyword
    } if (query.hasOwnProperty('status__in')) {
      state.filterInfo.status__in = query.status__in
    } if (query.hasOwnProperty('batch')) {
      state.filterInfo.batch = query.batch
    } if (query.hasOwnProperty('vendor')) {
      state.filterInfo.vendor = query.vendor
    } if (query.hasOwnProperty('pageCount')) {
      state.filterInfo.pageCount = query.pageCount
    } if (query.hasOwnProperty('batch__bank__currency__in')) {
      state.filterInfo.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [RECEIVE_AR] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Accounts Receivable'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_AR] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_AR_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_AR_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_ALL_AR_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [SAVING_AR_LINES] (state, payload) {
    state.savingLines = { status: payload.status, err: null }
  },
  [UPDATING_AR_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [INCREMENT_AR_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_AR_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_AR_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_AR_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_AR_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      deleting: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        for (let i = 0; i < payload.length; i++) {
          if (newState.hasOwnProperty(payload[i])) delete newState[payload[i]]
        }
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_AR_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_AR_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [DELETE_AR_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/ar.js